import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
} from 'recharts';
import useMedicationSelectionStore from './medicationSelectionStore';

const TaperingDescentChart = ({ initialDose = 40 }) => {
  const [durationWeeks, setDurationWeeks] = useState(8);
  const activeDrug = useMedicationSelectionStore((state) => state.activeDrug);

  const effectiveDose =
    typeof activeDrug?.doseMg === 'number' && activeDrug.doseMg > 0
      ? activeDrug.doseMg
      : initialDose;

  const data = useMemo(() => {
    const points = [];
    const steps = durationWeeks;
    const k = 3 / Math.max(steps, 1);

    for (let week = 0; week <= steps; week += 1) {
      const t = week;
      const fraction = Math.exp(-k * (t / steps));
      const dose = parseFloat((effectiveDose * fraction).toFixed(1));
      points.push({ week, dose });
    }

    return points;
  }, [durationWeeks, effectiveDose]);

  const maxDose = effectiveDose;
  const safeZoneUpper = effectiveDose * 0.4;

  return (
    <div className="flex flex-col gap-5 p-6 bg-white border-2 border-neo-main rounded-xl shadow-neo-brutal">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-neo-text-sub">
            Tapering Descent
          </p>
          <p className="text-sm font-bold text-neo-main mt-1">
            {activeDrug?.label
              ? `Dose trajectory for ${activeDrug.label}`
              : 'Dose trajectory over configurable taper duration'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-neo-success text-white border-2 border-neo-main shadow-neo-sm text-xs font-bold">
            <span className="w-2 h-2 rounded-full bg-white border border-neo-main" />
            Safe Zone
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-neo-warning text-neo-main border-2 border-neo-main shadow-neo-sm text-xs font-bold">
            <span className="w-2 h-2 rounded-full bg-neo-main" />
            Risk Zone
          </span>
        </div>
      </div>

      <div className="w-full h-72 rounded-xl bg-neo-bg border-2 border-neo-main relative overflow-hidden p-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
          >
            <CartesianGrid
              stroke="#2d3436"
              strokeDasharray="4 4"
              strokeOpacity={0.1}
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tick={{ fill: '#2d3436', fontSize: 11, fontWeight: 700 }}
              axisLine={{ stroke: '#2d3436', strokeWidth: 2 }}
              tickLine={{ stroke: '#2d3436', strokeWidth: 2 }}
              label={{
                value: 'TIME (WEEKS)',
                position: 'insideBottom',
                offset: -12,
                fill: '#2d3436',
                fontSize: 10,
                fontWeight: 900,
              }}
            />
            <YAxis
              tick={{ fill: '#2d3436', fontSize: 11, fontWeight: 700 }}
              axisLine={{ stroke: '#2d3436', strokeWidth: 2 }}
              tickLine={{ stroke: '#2d3436', strokeWidth: 2 }}
              label={{
                value: 'DOSAGE (MG)',
                angle: -90,
                position: 'insideLeft',
                offset: 14,
                fill: '#2d3436',
                fontSize: 10,
                fontWeight: 900,
              }}
              domain={[0, maxDose]}
            />
            <Tooltip
              cursor={{ stroke: '#2d3436', strokeWidth: 2, strokeDasharray: '4 4' }}
              contentStyle={{
                backgroundColor: '#ffffff',
                borderRadius: '0.5rem',
                border: '2px solid #2d3436',
                boxShadow: '4px 4px 0px #000000',
                padding: '8px 12px',
                fontWeight: 'bold',
              }}
              labelStyle={{ color: '#636e72', fontSize: 11, textTransform: 'uppercase', marginBottom: '4px' }}
              itemStyle={{ color: '#2d3436', fontSize: 13, fontWeight: 900 }}
              formatter={(value) => [`${value} mg`, 'Dosage']}
            />

            <ReferenceArea
              y1={0}
              y2={safeZoneUpper}
              stroke="none"
              fill="#00b894"
              fillOpacity={0.1}
            />
            <ReferenceArea
              y1={safeZoneUpper}
              y2={maxDose}
              stroke="none"
              fill="#fdcb6e"
              fillOpacity={0.15}
            />

            <Line
              type="monotone"
              dataKey="dose"
              stroke="#0984e3"
              strokeWidth={4}
              dot={{ r: 5, strokeWidth: 2, stroke: '#2d3436', fill: '#fff' }}
              activeDot={{ r: 8, strokeWidth: 2, stroke: '#2d3436', fill: '#0984e3' }}
              isAnimationActive
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-col gap-3 bg-neo-bg p-4 rounded-xl border-2 border-neo-main">
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold text-neo-main uppercase tracking-wider">Taper Duration</span>
          <span className="font-black text-lg text-neo-accent">
            {durationWeeks} <span className="text-sm text-neo-text-sub font-bold">WEEKS</span>
          </span>
        </div>
        <input
          type="range"
          min={4}
          max={16}
          step={1}
          value={durationWeeks}
          onChange={(e) => setDurationWeeks(parseInt(e.target.value, 10))}
          className="w-full accent-neo-accent h-2 bg-white rounded-lg appearance-none border-2 border-neo-main cursor-pointer"
        />
        <div className="flex items-center justify-between text-[10px] font-bold text-neo-text-sub uppercase tracking-wide">
          <span>Short (Assertive)</span>
          <span>Extended (Gentle)</span>
        </div>
      </div>
    </div>
  );
};

export default TaperingDescentChart;
